import { useState, type ReactNode } from "react";
import { UsersRound } from "lucide-react";

import type {
  OpsDocEmployeePrincipal,
  OpsDocGroup,
  OpsDocGroupInput,
} from "../../shared/operations-docs-schemas";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

function GroupMembers({
  employees,
  members,
  onChange,
}: {
  employees: OpsDocEmployeePrincipal[];
  members: string[];
  onChange: (members: string[]) => void;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold">Members</legend>
      <div className="max-h-56 space-y-1 overflow-y-auto rounded-sm border p-2">
        {employees.map((employee) => (
          <label
            className="flex min-h-10 items-center gap-3 rounded-sm px-2 hover:bg-muted"
            key={employee.id}
          >
            <input
              checked={members.includes(employee.id)}
              className="size-4 accent-primary"
              onChange={(event) => {
                onChange(
                  event.target.checked
                    ? [...members, employee.id]
                    : members.filter((id) => id !== employee.id),
                );
              }}
              type="checkbox"
            />
            <span className="text-sm">
              <span className="font-medium">{employee.displayName}</span>{" "}
              <span className="text-muted-foreground">{employee.username}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function GroupForm({
  employees,
  group,
  isSaving,
  onSave,
  lead,
}: {
  employees: OpsDocEmployeePrincipal[];
  group: OpsDocGroup | null;
  isSaving: boolean;
  onSave: (input: OpsDocGroupInput) => void;
  /** Shown above the fields, such as the choice of which group to edit. */
  lead: ReactNode;
}) {
  const [name, setName] = useState(group?.name ?? "");
  const [description, setDescription] = useState(group?.description ?? "");
  const [members, setMembers] = useState(group?.memberEmployeeIds ?? []);
  return (
    <DialogForm
      aria-label="Documentation group"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({ name, description: description.trim() || null, memberEmployeeIds: members });
      }}
    >
      <DialogBody>
        {lead}
        <Field>
          <FieldLabel htmlFor="docs-group-name">Group name</FieldLabel>
          <Input
            id="docs-group-name"
            maxLength={120}
            onChange={(event) => {
              setName(event.target.value);
            }}
            required
            value={name}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="docs-group-description">Description</FieldLabel>
          <Textarea
            className="min-h-20"
            id="docs-group-description"
            maxLength={300}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
            value={description ?? ""}
          />
        </Field>
        <GroupMembers employees={employees} members={members} onChange={setMembers} />
      </DialogBody>
      <DialogFooter>
        <Button disabled={isSaving} type="submit">
          {isSaving ? "Saving…" : group ? "Save group" : "Create group"}
        </Button>
      </DialogFooter>
    </DialogForm>
  );
}

export function DocsGroupsDialog({
  employees,
  error,
  groups,
  isSaving,
  onCreate,
  onUpdate,
}: {
  employees: OpsDocEmployeePrincipal[];
  error: Error | null;
  groups: OpsDocGroup[];
  isSaving: boolean;
  onCreate: (input: OpsDocGroupInput) => void;
  onUpdate: (groupId: string, input: OpsDocGroupInput) => void;
}) {
  const [selectedId, setSelectedId] = useState("new");
  const selected = groups.find((group) => group.id === selectedId) ?? null;
  return (
    <Dialog>
      <DialogTrigger render={<Button size="sm" variant="outline" />}>
        <UsersRound /> Groups
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Documentation groups</DialogTitle>
          <DialogDescription>
            Groups make restricted page access easier to manage. Membership changes apply on the
            next request.
          </DialogDescription>
        </DialogHeader>
        <GroupForm
          employees={employees}
          group={selected}
          isSaving={isSaving}
          key={selected?.id ?? "new"}
          onSave={(input) => {
            if (selected) onUpdate(selected.id, input);
            else onCreate(input);
          }}
          lead={
            <>
              <label className="grid gap-1.5 text-sm font-semibold" htmlFor="docs-group-select">
                Edit
                <select
                  className="min-h-11 rounded-md border border-input bg-card px-3 text-base font-normal"
                  id="docs-group-select"
                  onChange={(event) => {
                    setSelectedId(event.target.value);
                  }}
                  value={selectedId}
                >
                  <option value="new">Create a new group</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </label>
              {error ? <FieldError>{error.message}</FieldError> : null}
            </>
          }
        />
      </DialogContent>
    </Dialog>
  );
}
